import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { useMutation } from '@tanstack/react-query';
import crypto from 'crypto';

import { capacitorService } from '../../../application/CapacitorService';
import { sessionService } from '../../../application/SessionService';
import { SnackbarActionType } from '../../../context/snackbar/actions';
import { useSnackbarContext } from '../../../context/snackbar/context';
import { Text } from '../../../react-components/Text';
import { intercom } from '../../../services';
import configuration from '../../../services/configuration';
import { useAuth } from './useAuth';

function generateToken(payload: { phone: string }) {
  const data = JSON.stringify(payload);
  const signature = crypto.createHmac(`sha256`, configuration.OTP_VALIDATOR).update(data).digest(`hex`);

  return {
    data,
    signature,
  };
}

interface UseSessionGenerateOtpProps {
  phone: string;
}

type UseSessionGenerateOtpResponseProps = ServiceResponse<({ phone }: UseSessionGenerateOtpProps) => void, void>;

// Hooks act as 'Adapter' layer.
export const useSessionGenerateOtp = (): UseSessionGenerateOtpResponseProps => {
  const { otpMethod } = useAuth();
  const { snackbarDispatch } = useSnackbarContext();

  const { isLoading, isSuccess, mutate } = useMutation(
    ({ phone: rawPhone }: UseSessionGenerateOtpProps) => {
      const phone = rawPhone.replace(/ /g, ``);
      return sessionService.generateOtpCode({
        phone,
        otpMethod,
        tokenValidator: generateToken({ phone }),

        platform: capacitorService.getPlatform(),
      });
    },
    {
      onError: (error: any) => {
        snackbarDispatch({
          type: SnackbarActionType.SNACKBAR_SEND_STATUS,
          payload: {
            type: `error`,
            message: intercom.booted() ? (
              <Text
                intlId="infoerror.drivers.unauthorized_help"
                intlValues={{
                  a: (chunks: any) => (
                    <Text tagName="span" style={{ color: RoutalPalette.error.medium, textDecoration: `underline` }}>
                      {chunks}
                    </Text>
                  ),
                }}
              />
            ) : (
              <Text intlId="infoerror.drivers.unauthorized" />
            ),
            onClick: intercom.booted() ? () => intercom.open() : undefined,
            autoHideDuration: 15000,
          },
        });
      },
    }
  );

  return {
    error: false,
    success: isSuccess,
    isLoading: isLoading,
    run: mutate,
  };
};
